import { useEffect, useRef, useState, useMemo } from "react";
import { Grid, Typography } from "@mui/material";

import Layout from "../../layout/Layout";
import {
  fetchProductsAPI,
  fetchUnitsAPI,
  fetchBundlesAPI,
  deleteProductAPI,
} from "../../config";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useNavigate, useLocation } from "react-router-dom";
import { usePersistedState } from "../../utils/usePersistedState";
import { formatAED, formatNumber } from "../../utils/format";
import moment from "moment";
import { formatLocalDateTime } from "../../utils/datetime";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import InventoryIcon from "@mui/icons-material/Inventory";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../services/api_service";
import PillToggle from "../../components/PillToggle";

import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

const isBundle = (product) =>
  product?.color === "Bundle" || product?.size === "Bundle";

const getFirstProductImage = (image) => {
  try {
    const parsed = typeof image === "string" ? JSON.parse(image) : image;
    if (Array.isArray(parsed)) {
      return parsed.find((img) => typeof img === "string" && img.trim()) || "";
    }
  } catch {
    return "";
  }
  return "";
};

function ProductImageCell({ image, productName }) {
  const [loadError, setLoadError] = useState(false);

  const firstImage = !loadError ? getFirstProductImage(image) : "";

  const formatFileName = (src) => {
    if (!src) return "";
    const baseName = src.split("/").pop() || "";
    const withoutExt = baseName.replace(/\.[^/.]+$/, "");
    const parts = withoutExt.split("-");
    if (parts.length > 2) {
      return parts.slice(0, -2).join(" ").replace(/_/g, " ");
    }
    return withoutExt.replace(/_/g, " ");
  };

  const displayName = firstImage
    ? formatFileName(firstImage) || productName || "Image"
    : "No Image";

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: "50px",
          height: "50px",
          margin: "0 auto",
          borderRadius: "4px",
          overflow: "hidden",
          backgroundColor: "#f6f6f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {firstImage ? (
          <img
            src={firstImage}
            alt={displayName}
            onError={() => setLoadError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : (
          <Inventory2Icon
            style={{
              width: "40px",
              height: "40px",
              color: "#ccc",
            }}
          />
        )}
      </div>
      <div
        style={{
          color: "blue",
          fontSize: "12px",
          marginTop: "4px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "90px",
          margin: "4px auto 0",
        }}
      >
        {displayName}
      </div>
    </div>
  );
}

function ProductMasterList() {
  const navigate = useNavigate();
  const location = useLocation();

  const [totalCount, setTotalCount] = useState(0);
  const [productMasterList, setProductMasterList] = useState([]);

  // "all" shows every product (incl. bundle parents); "product" hides bundles;
  // "bundle" shows bundles with their component products (expandable).
  const [viewMode, setViewMode] = usePersistedState("product-list:viewMode", "all");
  const [bundleList, setBundleList] = useState([]);
  const [bundleLoading, setBundleLoading] = useState(false);
  // Item 5: the bundle whose "Total Products" dialog is currently open (or
  // null when closed) — holds the full bundle row, including `components`.
  const [bundleDetailDialog, setBundleDetailDialog] = useState(null);

  const handleEditStore = (id) => {
    navigate(`/product/edit/${id}`);
  };

  // Soft delete only — the row and all its order/stock/PO history stay
  // intact server-side (see deleteProduct in productMasterModel.js), it
  // just stops appearing here and in every product picker/dropdown.
  const handleDeleteProduct = async (row) => {
    const label = isBundle(row) ? "bundle" : "product";
    if (
      !window.confirm(
        `Delete "${row.product_name}"? Existing orders/POs/stock history won't be affected.`
      )
    ) {
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const result = await apiData("delete", `${deleteProductAPI}/${row.id}`, {
        user_id: user?.emp_id,
      });
      if (result.success) {
        setSuccessMessage(`Deleted ${label} successfully`);
        fetchUsers();
      } else {
        alert(result.msg || `Failed to delete ${label}`);
      }
    } catch (error) {
      console.error("Delete product failed:", error);
      alert(`Failed to delete ${label}`);
    }
  };

  const [loading, setLoading] = useState(false);

  const [pageIndex, setPageIndex] = usePersistedState("product-list:pageIndex", 0);
  const [pageSize, setPageSize] = usePersistedState("product-list:pageSize", 100);
  const [filters, setFilters] = usePersistedState("product-list:filters", () => {
    const alertFilter = location.state?.alertFilter;
    const categoryName = location.state?.categoryName;
    const searchVal = location.state?.search || location.state?.productName;
    if (alertFilter === "out_of_stock") {
      return [{ id: "out_of_stock", value: "1" }];
    } else if (alertFilter === "low_stock") {
      return [{ id: "low_stock", value: "1" }];
    } else if (alertFilter === "high_demand") {
      return [{ id: "high_demand", value: "1" }];
    } else if (alertFilter === "slow_moving") {
      return [{ id: "slow_moving", value: "1" }];
    } else if (alertFilter === "damaged_stock") {
      return [{ id: "damaged_stock", value: "1" }];
    } else if (categoryName) {
      return [{ id: "category_name", value: categoryName }];
    } else if (searchVal) {
      return [{ id: "product_name", value: searchVal }];
    }
    return [];
  });
  // Context passed from dashboard when navigating via an inventory alert card.
  // The backend uses these to recompute the exact same filters as the dashboard.
  const alertFromDate = location.state?.fromDate || null;
  const alertToDate   = location.state?.toDate   || null;
  const alertPeriod   = location.state?.period   || null;
  const alertStoreId  = location.state?.storeId  || null;
  const alertUserId   = location.state?.userId   || null;
  const [sorting, setSorting] = usePersistedState("product-list:sorting", []);
  const [unitsList, setUnitsList] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const headerButtonSx = {
    minHeight: 34,
    px: 1.5,
    py: 0.5,
    borderRadius: "8px",
    fontSize: "0.78rem",
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.18)",
    "& .MuiButton-startIcon": {
      mr: 0.75,
    },
  };

  let tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false) {
      fetchUsers(1);
    }
  }, []);

  const handleExportData = async () => {
    try {
      setLoading(true);

      const payload = {
        // Export every filtered row, not just the current page.
        pageIndex: 0,
        pageSize: 1000000,
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters.length > 0 ? JSON.stringify(filters[0]) : null,
        excludeBundles: viewMode === "product",
        ...(alertFromDate && { from_date: alertFromDate }),
        ...(alertToDate   && { to_date:   alertToDate   }),
        ...(alertPeriod   && { period:    alertPeriod   }),
        ...(alertStoreId  && { store_id:  alertStoreId  }),
        ...(alertUserId   && { user_id:   alertUserId   }),
      };

      const response = await apiData("get", fetchProductsAPI, payload);

      const data = response?.data?.result || [];

      if (data.length === 0) {
        alert("No data to export!");
        return;
      }

      const exportData = data.map((item, index) => ({
        "#": index + 1,
        "Product Image": getFirstProductImage(item.image) || "",
        "Product Name": isBundle(item)
          ? `${item.product_name} (Bundle)`
          : item.product_name,
        SKU: item.sku,
        UOM: item.uom_name,
        Color: isBundle(item) ? "" : item.color || "",
        Size: isBundle(item) ? "" : item.size || "",
        "Bar Code": item.bar_code || "",
        "Reorder Level":
          item.reorder_level === null || item.reorder_level === undefined || item.reorder_level === ""
            ? ""
            : Number(item.reorder_level),
        "Created By": item.created_by_name || "",
        "Created At": formatLocalDateTime(item.created_at),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "ProductsList");

      const fileName = `Product_List_${moment().format(
        "DD_MM_YYYY"
      )}.xlsx`;
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  // Export the bundle view: one row per bundle with its components summarised.
  // `table` (when given) is the bundle table on screen: export the bundles left after its filters.
  const handleExportBundles = (table) => {
    const bundlesToExport =
      table && typeof table.getPrePaginationRowModel === "function"
        ? table.getPrePaginationRowModel().rows.map((r) => r.original)
        : bundleList;
    if (!bundlesToExport.length) {
      alert("No bundles to export!");
      return;
    }
    // Each bundle is one row; its component products are nested sub-rows
    // (product + SKU + qty) beneath it instead of just a count.
    const HEADERS = [
      "#", "Bundle Name", "SKU", "Bundle Price", "Component Product",
      "Component SKU", "Qty", "Items", "Created At", "Valid From", "Valid To", "Status",
    ];
    const combined = [];
    const rowLevels = [];
    bundlesToExport.forEach((b, index) => {
      const comps = b.components || [];
      combined.push({
        "#": index + 1,
        "Bundle Name": b.product_name,
        SKU: b.sku,
        "Bundle Price": b.bundle_price,
        Items: comps.length,
        "Created At": formatLocalDateTime(b.created_at),
        "Valid From":
          b.limited_time_bundle && b.valid_from
            ? moment(b.valid_from).format("DD-MM-YYYY")
            : "-",
        "Valid To":
          b.limited_time_bundle && b.valid_to
            ? moment(b.valid_to).format("DD-MM-YYYY")
            : "-",
        Status: !b.limited_time_bundle
          ? "Active"
          : b.is_expired
          ? "Expired"
          : "Active",
      });
      rowLevels.push(0);
      comps.forEach((c) => {
        combined.push({
          "Component Product": c.product_name || "",
          "Component SKU": c.sku || "",
          Qty: c.required_per_bundle,
        });
        rowLevels.push(1);
      });
    });
    const ws = XLSX.utils.json_to_sheet(combined, { header: HEADERS });
    ws["!rows"] = [{}, ...rowLevels.map((lvl) => (lvl ? { level: 1 } : {}))];
    ws["!outline"] = { above: true };
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Bundles");
    writeXlsxFrozen(XLSX, wb, `Bundle_List_${moment().format("DD_MM_YYYY")}.xlsx`);
    setSuccessMessage("Excel Downloaded Successfully");
  };

  const fetchUsers = async () => {
    setLoading(true);
    const payload = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
      search: filters.length > 0 ? JSON.stringify(filters[0]) : null,
      excludeBundles: viewMode === "product",
      // Forward dashboard context so high_demand / slow_moving match the card count exactly.
      ...(alertFromDate && { from_date: alertFromDate }),
      ...(alertToDate   && { to_date:   alertToDate   }),
      ...(alertPeriod   && { period:    alertPeriod   }),
      ...(alertStoreId  && { store_id:  alertStoreId  }),
      ...(alertUserId   && { user_id:   alertUserId   }),
    };
    try {
      const response = await apiData("get", fetchProductsAPI, payload);
      setProductMasterList(response.data.result);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Bundle view has its own fetch; only reload the product grid for all/product.
    if (viewMode !== "bundle") fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, filters, sorting, viewMode]);

  // Bundles are loaded on demand (and only once needed) when the toggle is on
  // "bundle". No store filter here, so components carry qty but no stock number.
  useEffect(() => {
    if (viewMode !== "bundle") return;
    const loadBundles = async () => {
      setBundleLoading(true);
      try {
        const result = await apiData("get", fetchBundlesAPI);
        if (result.success) {
          setBundleList(result.data?.result || []);
        }
      } catch (error) {
        console.error("Failed to fetch bundles:", error);
        setBundleList([]);
      } finally {
        setBundleLoading(false);
      }
    };
    loadBundles();
  }, [viewMode]);

  useEffect(() => {
    const fetchUnits = async () => {
      const data = {
        intParentId: 88,
      };

      try {
        const result = await apiData("get", fetchUnitsAPI, data);
        if (result.success) {
          setUnitsList(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchUnits();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "image",
        header: "Product Image",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <ProductImageCell
            image={row.original.image}
            productName={row.original.product_name}
          />
        ),
      },
      {
        accessorKey: "product_name",
        header: "Product Name",
        size: 160,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <span>{row.original.product_name}</span>
            {isBundle(row.original) && (
              <Chip
                label="Bundle"
                size="small"
                color="secondary"
                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
              />
            )}
          </Box>
        ),
      },
      {
        accessorKey: "sku",
        header: "SKU",
        size: 100,
      },
      {
        accessorKey: "uom_name",
        header: "UOM",
        size: 70,
      },
      {
        accessorKey: "color",
        header: "Color",
        Cell: ({ row }) =>
          isBundle(row.original)
            ? "N/A"
            : row.original.color
            ? row.original.color
            : "-",
        size: 100,
      },
      {
        accessorKey: "size",
        header: "Size",
        Cell: ({ row }) =>
          isBundle(row.original)
            ? "N/A"
            : row.original.size
            ? row.original.size
            : "-",
        size: 80,
      },
      {
        accessorKey: "bar_code",
        header: "Bar Code",
        Cell: ({ row }) =>
          row.original.bar_code ? row.original.bar_code : "-",
        size: 100,
      },
      {
        accessorKey: "reorder_level",
        header: "Reorder Level",
        Cell: ({ cell }) => formatNumber(cell.getValue() || 0),
        size: 100,
      },
      {
        accessorKey: "created_by_name",
        header: "Created By",
        size: 130,
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ row }) => formatLocalDateTime(row.original.created_at),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex" }}>
            <Tooltip title={isBundle(row.original) ? "Click to Edit Bundle" : "Click to Edit"}>
              <IconButton
                color="primary"
                onClick={() =>
                  isBundle(row.original)
                    ? navigate(`/product/bundle-edit/${row.original.id}`)
                    : handleEditStore(row.original.id)
                }
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => handleDeleteProduct(row.original)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        enableColumnFilter: false,
      },
    ],
    []
  );

  const bundleColumns = useMemo(
    () => [
      {
        accessorKey: "product_name",
        header: "Bundle Name",
        size: 200,
      },
      {
        accessorKey: "sku",
        header: "Bundle Number",
        size: 120,
      },
      {
        accessorKey: "bundle_price",
        header: "Bundle Price",
        size: 120,
        Cell: ({ row }) => formatAED(row.original.bundle_price || 0),
      },
      {
        accessorKey: "components",
        header: "Total Products",
        size: 110,
        enableSorting: false,
        enableColumnFilter: false,
        // Item 5: clickable — opens a dialog with the bundle's product detail.
        Cell: ({ row }) => (
          <Button
            size="small"
            onClick={() => setBundleDetailDialog(row.original)}
            sx={{ textDecoration: "underline", minWidth: 0, p: 0 }}
          >
            {(row.original.components || []).length}
          </Button>
        ),
      },
      {
        accessorKey: "created_by_name",
        header: "Created By",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.created_by_name
            ? row.original.created_by_name 
            : "-",
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.created_at
            ? moment(row.original.created_at).format("DD-MM-YYYY")
            : "-",
      },
      {
        accessorKey: "valid_from",
        header: "Valid From",
        size: 110,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.limited_time_bundle && row.original.valid_from
            ? moment(row.original.valid_from).format("DD-MM-YYYY")
            : "-",
      },
      {
        accessorKey: "valid_to",
        header: "Valid To",
        size: 110,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.limited_time_bundle && row.original.valid_to
            ? moment(row.original.valid_to).format("DD-MM-YYYY")
            : "-",
      },
      {
        accessorKey: "is_expired",
        header: "Status",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          !row.original.limited_time_bundle ? (
            <Chip
              label="Active"
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11.5,
                height: 24,
                color: "#0f5132",
                backgroundColor: "#e7f6ec",
                border: "1px solid #b7e4c7",
              }}
            />
          ) : row.original.is_expired ? (
            <Chip
              label="Expired"
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11.5,
                height: 24,
                color: "#9f1239",
                backgroundColor: "#fff1f2",
                border: "1px solid #f8a9b6",
              }}
            />
          ) : (
            <Chip
              label="Active"
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11.5,
                height: 24,
                color: "#0f5132",
                backgroundColor: "#e7f6ec",
                border: "1px solid #b7e4c7",
              }}
            />
          ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex" }}>
            <Tooltip title="Click to Edit Bundle">
              <IconButton
                color="primary"
                onClick={() =>
                  navigate(`/product/bundle-edit/${row.original.id}`)
                }
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => handleDeleteProduct(row.original)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  // Shared grid of a bundle's component products — used by both the
  // expandable row detail AND the "Total Products" click dialog (Item 5) so
  // they never drift apart.
  const renderBundleComponentsGrid = (components) =>
    components.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No products in this bundle
      </Typography>
    ) : (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {components.map((c) => (
          <Box
            key={c.product_id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 2,
              py: 1.25,
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
              }}
            >
              <Inventory2Icon sx={{ color: "#94a3b8", fontSize: 24 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {c.product_name}
                </Typography>
                {c.sku ? (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    SKU: {c.sku}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Chip
              label={`Qty: ${c.required_per_bundle}`}
              size="small"
              sx={{
                fontWeight: 700,
                backgroundColor: "#e3f2fd",
                color: "#0c54a0",
                flexShrink: 0,
              }}
            />
          </Box>
        ))}
      </Box>
    );

  // Expandable row body for the bundle view: the component products + per-bundle
  // quantity. `components`/`required_per_bundle` come straight from the bundles API.
  const renderBundleDetailPanel = ({ row }) => (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 2, backgroundColor: "#f8fafc" }}>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ mb: 1.5, color: "#0c54a0", letterSpacing: 0.2 }}
      >
        Products in this bundle
      </Typography>
      {renderBundleComponentsGrid(row.original.components || [])}
    </Box>
  );

  // Products / Bundles view switch, shown inside each table's top toolbar.
  const viewToggle = (
    <PillToggle
      value={viewMode}
      onChange={setViewMode}
      options={[
        { value: "all", label: "All" },
        { value: "product", label: "Products" },
        { value: "bundle", label: "Bundle" },
      ]}
    />
  );

  // Visual props shared by both the product and bundle tables.
  const commonTableProps = {
    enableGlobalFilter: false,
    enableStickyHeader: true,
    enableFullScreenToggle: false,
    className: "mat-react-tbl",
    layoutMode: "grid",
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 240px)",
        minHeight: 450,
        overflowY: "auto",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: "3px 8px",
        border: "1px solid #ccc",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        padding: "8px",
        border: "1px solid #ccc",
        color: "#fff !important",
        fontWeight: "bold",
        backgroundColor: "#1976d2",
        "& svg": {
          color: "#fff !important",
        },
        "& .MuiTableSortLabel-root": {
          color: "#fff !important",
          "&.Mui-active": {
            color: "#fff !important",
            "& .MuiTableSortLabel-icon": {
              color: "#fff !important",
            },
          },
        },
        "& .MuiTableSortLabel-icon": {
          color: "#fff !important",
        },
        "& .MuiInputBase-input": {
          color: "#fff !important",
          textFillColor: "#fff !important",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#fff !important",
          opacity: 0.8,
        },
        "& .MuiInput-underline:before": {
          borderBottomColor: "#fff !important",
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          borderBottomColor: "#fff !important",
        },
        "& .MuiInput-underline:after": {
          borderBottomColor: "#fff !important",
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        borderBottom: "1px solid #ccc",
        backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
      },
    }),
    muiTableHeadCellFilterTextFieldProps: {
      sx: {
        "& input": {
          color: "#fff !important",
        },
        "& .MuiInput-underline:before": {
          borderBottomColor: "#fff !important",
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          borderBottomColor: "#fff !important",
        },
        "& .MuiInput-underline:after": {
          borderBottomColor: "#fff !important",
        },
      },
    },
  };

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Product List
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Product</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="center"
            justifyContent="flex-end"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <LoadingButton
              size="small"
              variant="contained"
              color="success"
              sx={headerButtonSx}
              onClick={() => navigate("/product/add")}
              startIcon={<AddIcon />}
            >
              Add Product
            </LoadingButton>

            <LoadingButton
              size="small"
              variant="contained"
              sx={{
                ...headerButtonSx,
                backgroundColor: "#0c54a0",
                "&:hover": { backgroundColor: "#083e78" },
              }}
              onClick={() => navigate("/product/bundle-add")}
              startIcon={<AddIcon />}
            >
              Add Bundle
            </LoadingButton>

             <LoadingButton
              size="small"
              variant="contained"
              sx={{
                ...headerButtonSx,
                backgroundColor: "#7B1FA2",
                "&:hover": { backgroundColor: "#6A1B9A" },
              }}
              onClick={() => navigate("/product/bulk-upload")}
              startIcon={<UploadFileIcon />}
            >
              Bulk Upload
            </LoadingButton>

            <LoadingButton
              size="small"
              variant="contained"
              color="primary"
              sx={{
                ...headerButtonSx,
              }}
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <Grid mt={2}>
          {viewMode === "bundle" ? (
            <MaterialReactTable
              // Distinct key from the product table below: both render at the
              // same spot in this ternary, so without unique keys React treats
              // them as one instance and MRT's internal columnOrder bleeds from
              // the product view (loaded first) into the bundle view — pushing
              // Edit ahead of the bundle-only columns. Separate keys give each
              // table its own state so columns follow their own definition order.
              key="bundle-table"
              {...commonTableProps}
              columns={bundleColumns}
              data={bundleList}
              enableExpanding
              renderDetailPanel={renderBundleDetailPanel}
              renderTopToolbarCustomActions={({ table }) => (
                <Box
                  sx={{
                    p: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Tooltip title="Export bundles to excel sheet" arrow>
                    <Button
                      onClick={() => handleExportBundles(table)}
                      style={{ background: "#1a73e829" }}
                      startIcon={<FileDownload />}
                    >
                      Export To Excel
                    </Button>
                  </Tooltip>
                  {viewToggle}
                </Box>
              )}
              state={{ isLoading: bundleLoading }}
              initialState={{ pagination: { pageSize: 25, pageIndex: 0 } }}
              muiTablePaginationProps={{
                rowsPerPageOptions: [25, 50, 100],
              }}
            />
          ) : (
            <MaterialReactTable
            key="product-table"
            columns={columns}
            data={productMasterList}
            enableGlobalFilter={false}
            enableRowNumbers
            enableStickyHeader
            enableFullScreenToggle={false}
            className="mat-react-tbl"
            layoutMode="grid"
            state={{
              isLoading: loading,
              pagination: { pageIndex, pageSize },
              sorting,
              columnFilters: filters,
            }}
            manualPagination
            //   initialState={{
            //     columnPinning: lgUp ? { right: ["present_employee", "ID"] } : {},
            //   }}
            manualSorting
            manualFiltering
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const paginationState = updater({
                pageIndex,
                pageSize,
              });
              const { pageIndex: newPageIndex, pageSize: newPageSize } =
                paginationState;
              setPageIndex(newPageIndex);
              setPageSize(newPageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowY: "auto",
              },
            }}
            muiTablePaginationProps={{
              rowsPerPageOptions: [100],
            }}
            muiTableBodyCellProps={{
              sx: {
                padding: "3px 8px",
                border: "1px solid #ccc",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                padding: "8px",
                border: "1px solid #ccc",
                color: "#fff !important",
                fontWeight: "bold",
                backgroundColor: "#1976d2",
                "& svg": {
                  color: "#fff !important",
                },
                "& .MuiTableSortLabel-root": {
                  color: "#fff !important",
                  "&.Mui-active": {
                    color: "#fff !important",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                    },
                  },
                },
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                  textFillColor: "#fff !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#fff !important",
                  opacity: 0.8,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important",
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                borderBottom: "1px solid #ccc",
                backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
              },
            })}
            muiTableHeadCellFilterTextFieldProps={{
              sx: {
                "& input": {
                  color: "#fff !important", // Change text color
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important", // Change underline color
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important", // Hover underline color
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important", // Focused underline color
                },
              },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    p: "0.5rem",
                    justifyContent: "space-between",
                    flexGrow: 1,

                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Tooltip title="Export data to excel sheet" arrow>
                      <Button
                        onClick={handleExportData}
                        style={{ background: "#1a73e829" }}
                        startIcon={<FileDownload />}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                    {viewToggle}
                  </Box>
                </Box>
              </>
            )}
          />
          )}
        </Grid>
      </Grid>

      {/* Item 5: Total Products click-through — same product grid as the
          expandable row, in a dialog. */}
      <Dialog
        open={Boolean(bundleDetailDialog)}
        onClose={() => setBundleDetailDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Products in {bundleDetailDialog?.product_name || "Bundle"}
          {bundleDetailDialog?.sku ? ` — ${bundleDetailDialog.sku}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {renderBundleComponentsGrid(bundleDetailDialog?.components || [])}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBundleDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default ProductMasterList;
